import React, { PureComponent } from "react";
import {
  Row,
  Col,
  Button,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
} from "reactstrap";
import classnames from "classnames";
import MPSBarLoader from "../../components/MPSBarLoader";
import RegionSelector from "../RegionSelector";
import Text from "../../components/Text";
import Messages from "../../Shared/Messages";
import I18nProvider from "../../services/I18nProvider";
import { getAPIURL } from "../../config";
import { devLog } from "../../utils/devLog";
const intl = I18nProvider.getIntlProvider();
const EVENT_KEY_DOWN = "keydown";
const EVENT_KEY_UP = "keyup";

class LoginForm extends PureComponent {
  constructor(props) {
    super(props);
    // Check if returning from Entra ID with a fallback (e.g., no facility mappings)
    const urlParams = new URLSearchParams(window.location.search);
    const entraFallback = urlParams.get("entra_fallback");
    const fallbackEmail = urlParams.get("email") || "";

    this.state = {
      isCapsLockActive: false,
      user: fallbackEmail || "",
      pass: "",
      passwordFieldAnimating: false,
      submitting: false,
      authMethod: entraFallback ? "password" : null,
      checkingAuthMethod: false,
      entraFallbackReason: entraFallback || null,
    };
    this.usernameInputRef = React.createRef();
    this.authCheckTimeout = null;
  }

  componentDidMount() {
    this.props.onResetLoading();

    // Clean up fallback params from URL without triggering navigation
    if (this.state.entraFallbackReason) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
    }

    let pass = document.getElementById("login-txt-pwd");
    if (pass) {
      pass.addEventListener(EVENT_KEY_DOWN, this.wasCapsLockActivated);
      pass.addEventListener(EVENT_KEY_UP, this.wasCapsLockDeactivated);
    }

    // Focus username field only when window is visible
    this.focusUsernameIfVisible();
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    window.addEventListener("focus", this.handleWindowFocus);
  }

  componentDidUpdate(prevProps) {
    // Reset submitting state when loading completes or there's an error
    if (prevProps.loading && !this.props.loading) {
      this.setState({ submitting: false });
    }
    if (
      prevProps.loginError !== this.props.loginError &&
      this.props.loginError
    ) {
      this.setState({ submitting: false });
    }
  }

  componentWillUnmount() {
    let pass = document.getElementById("login-txt-pwd");
    if (pass) {
      pass.removeEventListener(EVENT_KEY_DOWN, this.wasCapsLockActivated);
      pass.removeEventListener(EVENT_KEY_UP, this.wasCapsLockDeactivated);
    }
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );
    window.removeEventListener("focus", this.handleWindowFocus);

    // Clear timeout
    if (this.authCheckTimeout) {
      clearTimeout(this.authCheckTimeout);
    }

    // Clear sensitive data on unmount
    this.setState({ user: "", pass: "", viewPassword: false });
  }

  focusUsernameIfVisible = () => {
    if (
      document.visibilityState === "visible" &&
      !document.hidden &&
      this.usernameInputRef.current
    ) {
      this.usernameInputRef.current.focus();
    }
  };

  handleVisibilityChange = () => {
    this.focusUsernameIfVisible();
  };

  handleWindowFocus = () => {
    this.focusUsernameIfVisible();
  };

  wasCapsLockActivated = (event) => {
    if (
      event.getModifierState &&
      event.getModifierState("CapsLock") &&
      this.state.isCapsLockActive === false
    ) {
      this.setState({ isCapsLockActive: true });
    }
  };

  wasCapsLockDeactivated = (event) => {
    if (
      event.getModifierState &&
      !event.getModifierState("CapsLock") &&
      this.state.isCapsLockActive === true
    ) {
      this.setState({ isCapsLockActive: false });
    }
  };

  checkAuthMethod = async (email) => {
    if (!email || !email.includes("@")) {
      this.setState({ authMethod: null });
      return;
    }

    this.setState({ checkingAuthMethod: true });

    try {
      const response = await fetch(
        `${getAPIURL()}/api/auth/entra/login-method?email=${encodeURIComponent(
          email
        )}`
      );
      const data = await response.json();

      this.setState({
        authMethod: data.method,
        allowPasswordLogin: data.allowPasswordLogin !== false,
        checkingAuthMethod: false,
      });
    } catch (error) {
      devLog("Error checking auth method:", error);
      // Default to password on error to preserve existing functionality
      this.setState({
        authMethod: "password",
        checkingAuthMethod: false,
      });
    }
  };

  startEntraIdLogin = async () => {
    this.setState({ submitting: true });

    try {
      // Send redirectTo as form-encoded body rather than query string: Azure
      // AppGW's OWASP CRS 931 RFI rule 403s `://` in query params and strips
      // CORS headers on block, surfacing to the browser as a CORS error.
      const redirectTo = window.location.origin + "/auth-complete";
      const response = await fetch(
        `${getAPIURL()}/api/auth/entra/start-login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({ redirectTo }).toString(),
        }
      );
      const data = await response.json();

      // Redirect to Microsoft
      window.location.href = data.authorizationUrl;
    } catch (error) {
      devLog("Error starting Entra ID login:", error);
      this.setState({ submitting: false });
      alert("Failed to start Microsoft login. Please try again.");
    }
  };

  onLogin = async (e) => {
    e.preventDefault();

    // Check auth method if not already checked
    if (!this.state.authMethod && this.state.user.includes("@")) {
      await this.checkAuthMethod(this.state.user);
    }

    // If Entra ID only and user hasn't chosen password fallback, redirect to Microsoft
    if (this.state.authMethod === "entra_id" && !this.state.forcePassword) {
      this.startEntraIdLogin();
    } else {
      // Password login (works for both "password" and "both" modes)
      this.setState({ submitting: true });
      this.props.onLogin(this.state.user, this.state.pass);
    }
  };

  onUpdateUser = (e) => {
    const email = e.target.value;
    this.setState({ user: email });

    // Debounce the auth method check
    clearTimeout(this.authCheckTimeout);

    // Only check auth method when there's a complete domain (e.g., user@domain.com)
    // Check for @ followed by at least one character and a dot (basic domain validation)
    const hasCompleteDomain = /@.+\..+/.test(email);

    if (hasCompleteDomain) {
      this.authCheckTimeout = setTimeout(() => {
        this.checkAuthMethod(email);
      }, 500);
    } else {
      // Reset auth method if email is incomplete
      this.setState({ authMethod: null });
    }
  };

  onUpdatePass = (e) => {
    this.setState({ pass: e.target.value });
  };

  toggleViewPassword = () => {
    this.setState({ viewPassword: !this.state.viewPassword });
  };

  handlePasswordToggleKeyPress = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this.toggleViewPassword();
    }
  };

  onPasswordFocus = () => {
    this.setState({ passwordFieldAnimating: true });

    // Reset animation after 2 seconds
    setTimeout(() => {
      this.setState({ passwordFieldAnimating: false });
    }, 2000);
  };

  render() {
    let { loading, loginError } = this.props;
    let { submitting, authMethod, checkingAuthMethod, viewPassword } =
      this.state;
    let disabled = loading || submitting;
    const { forcePassword } = this.state;

    // Show password field unless entra_id only AND user hasn't clicked "use password"
    const showPasswordField = authMethod !== "entra_id" || forcePassword;

    return (
      <form onSubmit={this.onLogin} autoComplete="off">
        <style>
          {`
            @keyframes usernameFieldFocus {
              0% { background-color: #fff; }
              15% { background-color: #fff9c4; }
              85% { background-color: #fff9c4; }
              100% { background-color: #fff; }
            }
            @keyframes passwordFieldFocus {
              0% { background-color: #fff; }
              15% { background-color: #fff9c4; }
              85% { background-color: #fff9c4; }
              100% { background-color: #fff; }
            }
            .password-toggle-btn {
              transition: background-color 0.2s ease;
            }
            .password-toggle-btn:hover,
            .password-toggle-btn:focus {
              background-color: #f4e9aeff !important;
              outline: 2px solid #80bdff;
              outline-offset: -2px;
            }
          `}
        </style>
        {this.state.entraFallbackReason === "no_mappings" && (
          <div
            className="mb-3 p-3 rounded"
            style={{
              backgroundColor: "#fff3cd",
              border: "1px solid #ffc107",
              fontSize: "0.85rem",
            }}
          >
            <div className="d-flex align-items-start">
              <i
                className="fas fa-exclamation-triangle mr-2 mt-1"
                style={{ color: "#856404", flexShrink: 0 }}
              />
              <div>
                <div className="font-weight-bold" style={{ color: "#856404" }}>
                  <Text content={Messages.entra_no_facility_mappings_title} />
                </div>
                <div style={{ color: "#856404" }}>
                  <Text content={Messages.entra_no_facility_mappings_msg} />
                </div>
              </div>
            </div>
          </div>
        )}
        <InputGroup className="mb-3">
          <InputGroupAddon addonType="prepend">
            <InputGroupText
              className={classnames(loginError ? "is-invalid" : "")}
              style={{
                borderTopLeftRadius: 4,
                borderBottomLeftRadius: 4,
              }}
            >
              <i className="fa fa-user" />
            </InputGroupText>
          </InputGroupAddon>
          <Input
            innerRef={this.usernameInputRef}
            value={this.state.user}
            onChange={this.onUpdateUser}
            type="text"
            autoCapitalize="none"
            name="username"
            autoComplete="off"
            placeholder={intl.formatMessage(Messages.user_name)}
            disabled={disabled}
            className={classnames(
              loginError ? "is-invalid" : "",
              "username-field-focused"
            )}
            id="login-txt-username"
            maxLength="255"
            aria-label="Username"
            style={{
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              animation: "usernameFieldFocus 2s ease-in-out",
              fontSize: "16px",
              minHeight: "44px",
            }}
          />
        </InputGroup>

        {/* Password field - only show for non-Entra ID logins */}
        {showPasswordField && (
          <InputGroup>
            <InputGroupAddon addonType="prepend">
              <InputGroupText
                className={classnames(loginError ? "is-invalid" : "")}
                style={{
                  borderTopLeftRadius: 4,
                  borderBottomLeftRadius: 4,
                }}
              >
                <i className="fa fa-lock" />
              </InputGroupText>
            </InputGroupAddon>
            <Input
              value={this.state.pass}
              name="password"
              onChange={this.onUpdatePass}
              type={viewPassword ? "text" : "password"}
              placeholder={intl.formatMessage(Messages.password)}
              disabled={disabled}
              autoCapitalize="none"
              autoComplete="new-password"
              className={classnames(loginError ? "is-invalid" : "")}
              id="login-txt-pwd"
              onFocus={this.onPasswordFocus}
              maxLength="128"
              aria-label="Password"
              style={{
                borderRadius: 0,
                fontSize: "16px",
                minHeight: "44px",
                ...(this.state.passwordFieldAnimating && {
                  animation: "passwordFieldFocus 2s ease-in-out",
                }),
              }}
            />
            <div className="input-group-append">
              <span
                className="input-group-text cursor-pointer password-toggle-btn"
                onClick={this.toggleViewPassword}
                onKeyPress={this.handlePasswordToggleKeyPress}
                tabIndex="0"
                role="button"
                aria-label={viewPassword ? "Hide password" : "Show password"}
                style={{
                  borderTopRightRadius: 4,
                  borderBottomRightRadius: 4,
                }}
              >
                <i
                  className={`fa ${viewPassword ? "fa-eye-slash" : "fa-eye"}`}
                />
              </span>
            </div>
          </InputGroup>
        )}

        {/* Show info badge for Entra ID users (unless they chose password) */}
        {authMethod === "entra_id" && !forcePassword && (
          <div
            className="mb-3 mt-3 p-2 text-center"
            style={{
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            <i
              className="fab fa-microsoft mr-2"
              style={{ color: "#9e9e9e", fontSize: "14px" }}
            ></i>
            <span style={{ color: "#9e9e9e", fontSize: "0.85rem" }}>
              Microsoft account detected
            </span>
          </div>
        )}

        <div className="mb-4 mt-1 text-info" style={{ fontWeight: 500 }}>
          {showPasswordField && this.state.isCapsLockActive && (
            <Text content={Messages.caps_lock_on} />
          )}
        </div>

        <Row>
          <Col
            xs="12"
            className="d-flex align-items-center justify-content-end"
          >
            <Button
              type="submit"
              color="primary"
              className="px-4"
              disabled={disabled || checkingAuthMethod}
              id="login-btn-submit"
              aria-label="Log in"
              style={{
                width: "100%",
                padding: "0.5rem 0.75rem",
                fontWeight: 600,
                minHeight: "44px",
              }}
            >
              {loading || submitting ? (
                <div className="d-flex align-items-center justify-content-center">
                  <i
                    className="fas fa-spinner fa-spin mr-3"
                    style={{ fontSize: "16px" }}
                  ></i>
                  {authMethod === "entra_id" && !forcePassword ? (
                    "Redirecting to Microsoft..."
                  ) : (
                    <Text content={Messages.login} />
                  )}
                </div>
              ) : checkingAuthMethod ? (
                <div className="d-flex align-items-center justify-content-center">
                  <i
                    className="fas fa-spinner fa-spin mr-3"
                    style={{ fontSize: "16px" }}
                  ></i>
                  Checking...
                </div>
              ) : authMethod === "entra_id" && !forcePassword ? (
                <>
                  <i className="fab fa-microsoft mr-2"></i>
                  Continue with Microsoft
                </>
              ) : (
                <Text content={Messages.login} />
              )}
            </Button>
          </Col>
        </Row>

        {/* "Or sign in with Microsoft" — for "both" mode and password fallback mode */}
        {(authMethod === "both" || (authMethod === "entra_id" && forcePassword)) && (
          <div className="mt-3">
            <div className="text-center mb-2">
              <small className="text-muted">or</small>
            </div>
            <Button
              type="button"
              color="light"
              className="w-100"
              disabled={disabled}
              onClick={() => {
                this.setState({ forcePassword: false });
                this.startEntraIdLogin();
              }}
              style={{
                border: "1px solid #dadce0",
                fontWeight: 500,
                minHeight: "44px",
              }}
            >
              <i className="fab fa-microsoft mr-2" style={{ color: "#2196f3" }}></i>
              Sign in with Microsoft
            </Button>
          </div>
        )}

        {/* "Sign in with password instead" — escape hatch when Entra-only and backend allows it */}
        {authMethod === "entra_id" && !forcePassword && this.state.allowPasswordLogin && (
          <div className="mt-3 text-center">
            <button
              type="button"
              className="btn btn-sm w-100"
              onClick={() => this.setState({ forcePassword: true })}
              style={{
                border: "1px solid #e0e0e0",
                backgroundColor: "transparent",
                color: "#757575",
                fontSize: "0.85rem",
                fontWeight: 500,
                minHeight: "38px",
              }}
            >
              <i className="fa fa-lock mr-2" style={{ fontSize: "0.8rem" }}></i>
              Sign in with password instead
            </button>
          </div>
        )}
      </form>
    );
  }
}

export default LoginForm;
